/**
 * src/services/api.ts
 * Unified Edge API client for CKAudit v2
 */

export interface TransactionFilterParams {
  user_id?: number | string;
  type?: 'paid' | 'receive' | '';
  category_id?: number | string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface TransactionItem {
  id: number;
  user_id: number;
  username: string;
  user_avatar_url?: string | null;
  user_full_name?: string | null;
  transaction_type: 'paid' | 'receive';
  amount: number;
  category_id: number | null;
  category_name: string | null;
  description: string;
  transaction_date: string;
  due_date: string | null;
  receipt_url: string | null;
  running_balance?: number;
  created_by: number | null;
  created_at: string;
}

export interface UserItem {
  id: number;
  username: string;
  full_name?: string | null;
  whatsapp_phone?: string | null;
  role?: 'admin' | 'user';
  current_balance?: number;
  avatar_url?: string | null;
  upi_id?: string | null;
  is_active?: number;
  created_at?: string;
}

export interface CategoryItem {
  id: number;
  name: string;
  icon: string;
  color: string;
  is_default: number;
  transaction_count?: number;
}

export interface ApprovalItem {
  id: number;
  user_id: number;
  transaction_type: 'paid' | 'receive';
  amount: number;
  category_name: string | null;
  description: string;
  transaction_date: string;
  receipt_url: string | null;
  status: 'pending' | 'approved' | 'rejected';
  review_note: string | null;
  reviewer_id: number | null;
  reviewed_at: string | null;
  created_at: string;
  submitter_name: string;
  submitter_avatar?: string | null;
  reviewer_name?: string | null;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('ckaudit_jwt');
  const headers = new Headers(options.headers || {});

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(endpoint, {
    ...options,
    headers
  });

  const json = (await res.json().catch(() => null)) as any;

  if (res.status === 401) {
    localStorage.removeItem('ckaudit_jwt');
    localStorage.removeItem('ckaudit_user');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
  }

  if (!res.ok || (json && json.success === false)) {
    throw new Error(json?.error || `Request failed with status ${res.status}`);
  }

  return json?.data !== undefined ? json.data : json;
}

// In-flight promise map for request deduplication across simultaneous component mounts
const inFlightMap = new Map<string, Promise<any>>();
// Short-term in-memory client cache
const clientCache = new Map<string, { data: any; expiry: number }>();

function cachedRequest<T>(endpoint: string, ttlMs: number = 20000): Promise<T> {
  const cached = clientCache.get(endpoint);
  if (cached && cached.expiry > Date.now()) {
    return Promise.resolve(cached.data as T);
  }

  const existingInFlight = inFlightMap.get(endpoint);
  if (existingInFlight) {
    return existingInFlight as Promise<T>;
  }

  const promise = request<T>(endpoint)
    .then(data => {
      clientCache.set(endpoint, { data, expiry: Date.now() + ttlMs });
      return data;
    })
    .finally(() => {
      inFlightMap.delete(endpoint);
    });

  inFlightMap.set(endpoint, promise);
  return promise;
}

export function invalidateApiCache(prefix?: string) {
  if (!prefix) {
    clientCache.clear();
  } else {
    for (const key of clientCache.keys()) {
      if (key.startsWith(prefix)) {
        clientCache.delete(key);
      }
    }
  }
}

export const api = {
  // Approvals API
  approvals: {
    list: (params: { status?: string; user_id?: number | string; limit?: number; offset?: number } = {}) => {
      const query = new URLSearchParams();
      if (params.status) query.set('status', params.status);
      if (params.user_id) query.set('user_id', String(params.user_id));
      if (params.limit) query.set('limit', String(params.limit));
      if (params.offset) query.set('offset', String(params.offset));

      return request<{
        requests: ApprovalItem[];
        total: number;
        limit: number;
        offset: number;
      }>(`/api/approvals?${query.toString()}`);
    },

    count: () => {
      return cachedRequest<{ pendingCount: number }>('/api/approvals/count', 12000);
    },

    create: async (data: {
      transaction_type: 'paid' | 'receive';
      amount: number;
      description: string;
      category_name?: string;
      transaction_date?: string;
      receipt_url?: string;
      user_id?: number;
    }) => {
      invalidateApiCache('/api/approvals');
      return request<ApprovalItem>('/api/approvals', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    review: async (id: number, data: { decision: 'approve' | 'reject'; review_note?: string }) => {
      invalidateApiCache('/api/approvals');
      invalidateApiCache('/api/users');
      return request<{
        id: number;
        status: 'approved' | 'rejected';
        transaction_id?: number;
        updated_balance?: number;
      }>(`/api/approvals/${id}/review`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    }
  },

  // Transactions API
  transactions: {
    list: (params: TransactionFilterParams = {}) => {
      const query = new URLSearchParams();
      if (params.user_id) query.set('user_id', String(params.user_id));
      if (params.type) query.set('type', params.type);
      if (params.category_id) query.set('category_id', String(params.category_id));
      if (params.search) query.set('search', params.search);
      if (params.startDate) query.set('startDate', params.startDate);
      if (params.endDate) query.set('endDate', params.endDate);
      if (params.limit) query.set('limit', String(params.limit));
      if (params.offset) query.set('offset', String(params.offset));

      return request<{
        transactions: TransactionItem[];
        pagination: { total: number; limit: number; offset: number; hasMore: boolean };
        summary: { totalReceived: number; totalPaid: number; netDifference: number };
      }>(`/api/transactions?${query.toString()}`);
    },

    create: async (data: {
      user_id: number;
      transaction_type: 'paid' | 'receive';
      amount: number;
      description: string;
      category_id?: number | null;
      category_name?: string | null;
      transaction_date?: string;
      due_date?: string | null;
    }) => {
      invalidateApiCache('/api/users');
      invalidateApiCache('/api/categories');
      return request<{
        id: number;
        new_balance: number;
        amount: number;
      }>('/api/transactions', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    update: async (id: number, data: Partial<TransactionItem>) => {
      invalidateApiCache('/api/users');
      return request<{ success: boolean; message: string }>(`/api/transactions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    delete: async (id: number) => {
      invalidateApiCache('/api/users');
      return request<{ success: boolean; message: string }>(`/api/transactions/${id}`, {
        method: 'DELETE'
      });
    }
  },

  // Users API
  users: {
    list: () => {
      return cachedRequest<{ users: UserItem[] }>('/api/users', 30000);
    },

    create: async (data: {
      username: string;
      password: string;
      role?: 'admin' | 'user';
      full_name?: string | null;
      whatsapp_phone?: string | null;
      avatar_url?: string | null;
      upi_id?: string | null;
      initial_balance?: number;
    }) => {
      invalidateApiCache('/api/users');
      return request<UserItem>('/api/users', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    update: async (id: number, data: Partial<UserItem> & { password?: string }) => {
      invalidateApiCache('/api/users');
      return request<{ success: boolean; message: string }>(`/api/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    updateProfile: async (data: {
      avatar_url?: string | null;
      upi_id?: string | null;
      full_name?: string | null;
      whatsapp_phone?: string | null;
    }) => {
      invalidateApiCache('/api/users');
      return request<{ user: UserItem }>('/api/users/profile', {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    changePassword: async (id: number, password: string) => {
      invalidateApiCache('/api/users');
      return request<{ success: boolean; message: string }>(`/api/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ password })
      });
    }
  },

  // Auth API
  auth: {
    changePassword: (data: { new_password: string; current_password?: string }) => {
      return request<{ success: boolean; message: string }>('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    }
  },

  // Categories API (Category Master)
  categories: {
    list: () => {
      return cachedRequest<{ categories: CategoryItem[] }>('/api/categories', 60000);
    },

    create: async (data: { name: string; icon: string; color: string }) => {
      invalidateApiCache('/api/categories');
      return request<{ category: CategoryItem }>('/api/categories', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    update: async (id: number, data: { name?: string; icon?: string; color?: string }) => {
      invalidateApiCache('/api/categories');
      return request<{ category: CategoryItem }>(`/api/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    delete: async (id: number) => {
      invalidateApiCache('/api/categories');
      return request<{ success: boolean; message: string }>(`/api/categories/${id}`, {
        method: 'DELETE'
      });
    }
  },

  // Uploads API (Cloudflare R2 receipts)
  uploads: {
    receipt: (file: File | { data: string; filename?: string }) => {
      if (file instanceof File) {
        const formData = new FormData();
        formData.append('file', file);
        return request<{ receiptUrl: string; storage: string }>('/api/uploads/receipt', {
          method: 'POST',
          body: formData
        });
      } else {
        return request<{ receiptUrl: string; storage: string }>('/api/uploads/receipt', {
          method: 'POST',
          body: JSON.stringify(file)
        });
      }
    }
  },

  // Notifications & Reminders API
  notifications: {
    list: () => request<{ notifications: any[] }>('/api/notifications'),
    markAllRead: () => request<{ success: boolean }>('/api/notifications', { method: 'POST' }),
    clearAll: () => request<{ success: boolean }>('/api/notifications', { method: 'DELETE' }),
    delete: (id: string | number) => request<{ success: boolean }>(`/api/notifications?id=${id}`, { method: 'DELETE' }),
    remind: (data: { target_user_id: number; amount: number; note?: string }) =>
      request<{ success: boolean; message: string }>('/api/notifications/remind', {
        method: 'POST',
        body: JSON.stringify(data)
      })
  }
};

